// @sync-source: website-seniorthrive/src/lib/cookie-consent-core.ts
// Shared cookie consent logic, identical copy used in seniorthrivesaas

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConsentMethod = 'accept_all' | 'reject_all' | 'custom' | 'gpc_auto';

export interface CookieConsentState {
  version: 1;
  timestamp: string;
  expiresAt: string;
  tier1: true;
  tier2: boolean;
  tier3: boolean;
  gpcDetected: boolean;
  method: ConsentMethod;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'cookie_consent';
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

// GA4 measurement ID, used to set the disable flag on revocation
const GA4_ID = 'G-2PNZHTHLS9';

// Custom event dispatched on same-tab consent changes so React consumers
// (Intercom init, analytics, etc.) can re-evaluate without a page reload.
// Cross-tab changes are already observable via the native 'storage' event.
export const CONSENT_CHANGED_EVENT = 'cookie-consent-changed';

function emitConsentChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  } catch {
    // no-op, environments without CustomEvent support are fine without this
  }
}

// ---------------------------------------------------------------------------
// Core helpers
// ---------------------------------------------------------------------------

export function isGPCEnabled(): boolean {
  try {
    return (navigator as any).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

export function getConsentState(): CookieConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const state: CookieConsentState = JSON.parse(raw);

    // Validate version
    if (state.version !== 1) return null;

    // Check expiry
    if (new Date(state.expiresAt) < new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return state;
  } catch {
    return null;
  }
}

export function setConsentState(
  choices: Pick<CookieConsentState, 'tier2' | 'tier3' | 'method'>
): CookieConsentState {
  const now = new Date();
  const state: CookieConsentState = {
    version: 1,
    timestamp: now.toISOString(),
    expiresAt: new Date(now.getTime() + ONE_YEAR_MS).toISOString(),
    tier1: true,
    tier2: choices.tier2,
    tier3: choices.tier3,
    gpcDetected: isGPCEnabled(),
    method: choices.method,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  emitConsentChanged();
  return state;
}

export function hasValidConsent(): boolean {
  return getConsentState() !== null;
}

export function isTierAllowed(tier: 1 | 2 | 3): boolean {
  if (tier === 1) return true;
  const state = getConsentState();
  if (!state) return false;
  return tier === 2 ? state.tier2 : state.tier3;
}

export function clearConsent(): void {
  localStorage.removeItem(STORAGE_KEY);
  emitConsentChanged();
}

// ---------------------------------------------------------------------------
// Script injection (called from index.html inline script or React)
// ---------------------------------------------------------------------------

export function injectTier3Scripts(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Google Tag Manager
  if (!document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const gtm = document.createElement('script');
    gtm.async = true;
    gtm.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-KHX9LH2F';
    document.head.appendChild(gtm);
  }

  // Google Analytics (gtag.js)
  if (!document.querySelector(`script[src*="gtag/js?id=${GA4_ID}"]`)) {
    const ga = document.createElement('script');
    ga.async = true;
    ga.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(ga);

    // Remove disable flag if it was set during revocation
    delete (window as any)[`ga-disable-${GA4_ID}`];

    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]) {
      (window as any).dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', GA4_ID);
  }

  // Reddit Pixel
  if (!(window as any).rdt) {
    const w = window as any;
    const p = (w.rdt = function () {
      p.sendEvent
        ? p.sendEvent.apply(p, arguments)
        : p.callQueue.push(arguments);
    });
    p.callQueue = [];
    const t = document.createElement('script');
    t.src = 'https://www.redditstatic.com/ads/pixel.js';
    t.async = true;
    document.head.appendChild(t);
    w.rdt('init', 'a2_i0oynyzzf2nz', { optOut: false, useDecimalCurrencyValues: true });
    w.rdt('track', 'PageVisit');
  }
}

export function injectTier2Scripts(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // UserWay accessibility widget
  if (!document.querySelector('script[data-account="rHXkXFZIyA"]')) {
    const uw = document.createElement('script');
    uw.setAttribute('data-account', 'rHXkXFZIyA');
    uw.setAttribute('src', 'https://cdn.userway.org/widget.js');
    document.head.appendChild(uw);

    (window as any).interdeal = {
      sitekey: 'rHXkXFZIyA',
      Position: 'Left',
      Menulang: 'EN',
      domains: {
        js: 'https://cdn.userway.org/',
        acc: 'https://api.userway.org/',
      },
      color: { main: '#3575E2', second: '#3399FF' },
    };
  }
}

export function revokeTier3(): void {
  // Disable GA4, official method
  (window as any)[`ga-disable-${GA4_ID}`] = true;

  // Neutralize Reddit Pixel, replace with no-op so direct window.rdt() calls are harmless
  if ((window as any).rdt) {
    (window as any).rdt = function () {};
  }
}
